import React, { useState, useEffect } from "react";
import { Modal, FormActions } from "../../shared";
import { Icons } from "../../shared/Icons";
import {
  professionalsApi,
  Professional,
} from "../../../services/professionalsApi";
import { servicesApi, authService } from "../../../services";
import type { Service } from "../../../types";

interface ServiceFormData {
  professionalId: number | null;
  name: string;
  description: string;
  price: string;
  /** Effort.LEVE → priceLight */
  priceLight: string;
  /** Effort médio → priceMedium */
  priceMedium: string;
  /** Effort pesado → priceHeavy */
  priceHeavy: string;
  duration: string;
  amount: string;
  type: string;
  expirationDays: string;
}

interface CreateServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  companyCategory?: "car" | "barbershop";
  editingService?: Service | null;
}

export const CreateServiceModal: React.FC<CreateServiceModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  companyCategory,
  editingService,
}) => {
  const userData = authService.getUserData();
  const userType = userData?.userType;
  const isProfessional = userType === "professional";
  const loggedInProfessionalId = isProfessional ? userData?.id : null;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [professionals, setProfessionals] = useState<Professional[]>([]);

  const [formData, setFormData] = useState<ServiceFormData>({
    professionalId: loggedInProfessionalId || null,
    name: "",
    description: "",
    price: "0",
    priceLight: "",
    priceMedium: "",
    priceHeavy: "",
    duration: "30",
    amount: "1",
    type: companyCategory === "barbershop" ? "time" : "day",
    expirationDays: "",
  });

  useEffect(() => {
    if (isOpen) {
      fetchProfessionals();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isProfessional, loggedInProfessionalId]);

  useEffect(() => {
    if (editingService) {
      setFormData({
        professionalId: editingService.professionalId,
        name: editingService.name,
        description: editingService.description || "",
        price: editingService.price.toString(),
        priceLight:
          editingService.priceLight != null
            ? String(editingService.priceLight)
            : "",
        priceMedium:
          editingService.priceMedium != null
            ? String(editingService.priceMedium)
            : "",
        priceHeavy:
          editingService.priceHeavy != null
            ? String(editingService.priceHeavy)
            : "",
        duration: (editingService.duration || 30).toString(),
        amount: (editingService.amount || 1).toString(),
        type: editingService.type,
        expirationDays:
          editingService.expirationDays != null
            ? String(editingService.expirationDays)
            : "",
      });
    } else {
      // When creating a new service, set logged-in professional as default if user is professional
      setFormData({
        professionalId: loggedInProfessionalId || null,
        name: "",
        description: "",
        price: "",
        priceLight: "",
        priceMedium: "",
        priceHeavy: "",
        duration: "",
        amount: "",
        type: companyCategory === "barbershop" ? "time" : "day",
        expirationDays: "",
      });
    }
    setError(null);
  }, [editingService, isOpen, loggedInProfessionalId, companyCategory]);

  const fetchProfessionals = async () => {
    try {
      // If user is a professional, fetch only their own data
      if (isProfessional && loggedInProfessionalId) {
        const response = await professionalsApi.getProfessionalById(loggedInProfessionalId);
        if (response.success && response.data) {
          setProfessionals([response.data]);
        }
      } else {
        // If user is company admin, fetch all professionals
        const response = await professionalsApi.getProfessionalsByCompany();
        if (response.success && response.data) {
          setProfessionals(response.data);
        }
      }
    } catch (err) {
      console.error("Failed to fetch professionals:", err);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.name.trim()) {
      setError("Nome do serviço é obrigatório");
      return;
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      setError("Preço deve ser maior que zero");
      return;
    }
    if (
      formData.type === "day" &&
      (!formData.amount || parseInt(formData.amount) <= 0)
    ) {
      setError("Quantidade deve ser maior que zero");
      return;
    }
    if (
      formData.type === "time" &&
      (!formData.duration || parseInt(formData.duration) <= 0)
    ) {
      setError("Duração deve ser maior que zero");
      return;
    }

    const expirationTrimmed = formData.expirationDays.trim();
    if (expirationTrimmed !== "") {
      const expirationDays = parseInt(expirationTrimmed, 10);
      if (isNaN(expirationDays) || expirationDays <= 0) {
        setError("Validade deve ser um número de dias maior que zero");
        return;
      }
    }

    const parseOptionalEffortPrice = (
      raw: string
    ): number | null | "invalid" => {
      const t = raw.trim();
      if (t === "") return null;
      const n = parseFloat(t);
      if (isNaN(n) || n <= 0) return "invalid";
      return n;
    };

    if (companyCategory === "car") {
      const pl = parseOptionalEffortPrice(formData.priceLight);
      const pm = parseOptionalEffortPrice(formData.priceMedium);
      const ph = parseOptionalEffortPrice(formData.priceHeavy);
      if (pl === "invalid" || pm === "invalid" || ph === "invalid") {
        setError(
          "Preços por esforço do veículo, quando preenchidos, devem ser maiores que zero"
        );
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const serviceData: any = {
        professionalId: formData.professionalId,
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        type: formData.type,
        isActive: true,
      };

      // Add amount for day type, duration for time type
      if (formData.type === "day") {
        serviceData.amount = parseInt(formData.amount);
      } else {
        serviceData.duration = parseInt(formData.duration);
      }

      if (companyCategory === "car") {
        const toNullable = (raw: string): number | null => {
          const t = raw.trim();
          if (t === "") return null;
          return parseFloat(t);
        };
        serviceData.priceLight = toNullable(formData.priceLight);
        serviceData.priceMedium = toNullable(formData.priceMedium);
        serviceData.priceHeavy = toNullable(formData.priceHeavy);
      }

      serviceData.expirationDays =
        expirationTrimmed === "" ? null : parseInt(expirationTrimmed, 10);

      let response;
      if (editingService) {
        response = await servicesApi.updateService(
          editingService.id,
          serviceData
        );
      } else {
        response = await servicesApi.createService(serviceData);
      }

      if (response.success) {
        setLoading(false);
        onSuccess();
        onClose();
      } else {
        setError(response.error || "Falha ao salvar serviço");
        setLoading(false);
      }
    } catch (err) {
      setError("Falha ao salvar serviço");
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      professionalId: null,
      name: "",
      description: "",
      price: "0",
      priceLight: "",
      priceMedium: "",
      priceHeavy: "",
      duration: "30",
      amount: "1",
      type: "day",
      expirationDays: "",
    });
    setError(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={editingService ? "Editar Serviço" : "Criar Novo Serviço"}
      icon={companyCategory === "car" ? <Icons.Build sx={{ fontSize: 24 }} /> : <Icons.ContentCut sx={{ fontSize: 24 }} />}
    >
      {error && (
        <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center gap-3 mb-6">
          <Icons.Warning sx={{ fontSize: 24, color: "#b91c1c" }} />
          <span className="text-red-800 dark:text-red-200 font-medium">{error}</span>
        </div>
      )}

      <div className="space-y-6">
        {/* Service Name */}
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2"
          >
            Nome do Serviço *
          </label>
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSubmit();
              } else if (e.key === "Escape") {
                handleClose();
              }
            }}
            required
            disabled={loading}
            className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 
                     focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 
                     bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100
                     disabled:bg-gray-50 dark:disabled:bg-slate-800 disabled:cursor-not-allowed"
            placeholder={
              companyCategory === "car" ? "Limpeza de carro" : "Corte de cabelo"
            }
          />
        </div>

        {/* Description */}
        <div>
          <label
            htmlFor="description"
            className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2"
          >
            Descrição
          </label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            disabled={loading}
            rows={3}
            className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 
                     focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 
                     bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100
                     disabled:bg-gray-50 dark:disabled:bg-slate-800 disabled:cursor-not-allowed resize-none"
            placeholder="Descreva o que inclui este serviço..."
          />
        </div>

        {/* Price and Amount/Duration */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {formData.type === "day" ? (
            <div>
              <label
                htmlFor="amount"
                className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2"
              >
                Quantidade *
              </label>
              <input
                type="number"
                id="amount"
                min="1"
                value={formData.amount}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    amount: e.target.value,
                  })
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSubmit();
                  } else if (e.key === "Escape") {
                    handleClose();
                  }
                }}
                required
                disabled={loading}
                className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 
                         focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 
                         bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100
                         disabled:bg-gray-50 dark:disabled:bg-slate-800 disabled:cursor-not-allowed"
                placeholder="1"
              />
            </div>
          ) : (
            <div>
              <label
                htmlFor="duration"
                className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2"
              >
                Duração (min) *
              </label>
              <input
                type="number"
                id="duration"
                min="1"
                value={formData.duration}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    duration: e.target.value,
                  })
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSubmit();
                  } else if (e.key === "Escape") {
                    handleClose();
                  }
                }}
                required
                disabled={loading}
                className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 
                         focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 
                         bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100
                         disabled:bg-gray-50 dark:disabled:bg-slate-800 disabled:cursor-not-allowed"
                placeholder="60"
              />
            </div>
          )}

          <div>
            <label
              htmlFor="price"
              className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2"
            >
              {companyCategory === "car"
                ? "Preço padrão (R$) *"
                : "Preço (R$) *"}
            </label>
            <input
              type="number"
              id="price"
              min="0"
              step="0.01"
              value={formData.price}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  price: e.target.value,
                })
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSubmit();
                } else if (e.key === "Escape") {
                  handleClose();
                }
              }}
              required
              disabled={loading}
              className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 
                       focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 
                       bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100
                       disabled:bg-gray-50 dark:disabled:bg-slate-800 disabled:cursor-not-allowed"
              placeholder="0.00"
            />
            {companyCategory === "car" && (
              <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
                Usado quando não houver preço específico por esforço do veículo.
              </p>
            )}
          </div>
        </div>

        {companyCategory === "car" && (
          <div className="space-y-4">
            <p className="text-sm font-medium text-gray-700 dark:text-slate-300">
              Preço por esforço do veículo (opcional)
            </p>
            <p className="text-xs text-gray-500 dark:text-slate-400 -mt-2">
              Se preenchido, substitui o preço padrão para o esforço correspondente
              (leve / médio / pesado).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label
                  htmlFor="priceLight"
                  className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2"
                >
                  Esforço leve (R$)
                </label>
                <input
                  type="number"
                  id="priceLight"
                  min="0"
                  step="0.01"
                  value={formData.priceLight}
                  onChange={(e) =>
                    setFormData({ ...formData, priceLight: e.target.value })
                  }
                  disabled={loading}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 
                           focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 
                           bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100
                           disabled:bg-gray-50 dark:disabled:bg-slate-800 disabled:cursor-not-allowed"
                  placeholder="Opcional"
                />
              </div>
              <div>
                <label
                  htmlFor="priceMedium"
                  className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2"
                >
                  Esforço médio (R$)
                </label>
                <input
                  type="number"
                  id="priceMedium"
                  min="0"
                  step="0.01"
                  value={formData.priceMedium}
                  onChange={(e) =>
                    setFormData({ ...formData, priceMedium: e.target.value })
                  }
                  disabled={loading}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 
                           focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 
                           bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100
                           disabled:bg-gray-50 dark:disabled:bg-slate-800 disabled:cursor-not-allowed"
                  placeholder="Opcional"
                />
              </div>
              <div>
                <label
                  htmlFor="priceHeavy"
                  className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2"
                >
                  Esforço pesado (R$)
                </label>
                <input
                  type="number"
                  id="priceHeavy"
                  min="0"
                  step="0.01"
                  value={formData.priceHeavy}
                  onChange={(e) =>
                    setFormData({ ...formData, priceHeavy: e.target.value })
                  }
                  disabled={loading}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 
                           focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 
                           bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100
                           disabled:bg-gray-50 dark:disabled:bg-slate-800 disabled:cursor-not-allowed"
                  placeholder="Opcional"
                />
              </div>
            </div>
          </div>
        )}

        {/* Expiration days (optional) — used for client follow-up */}
        <div>
          <label
            htmlFor="expirationDays"
            className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2"
          >
            Validade (dias)
          </label>
          <input
            type="number"
            id="expirationDays"
            min="1"
            value={formData.expirationDays}
            onChange={(e) =>
              setFormData({ ...formData, expirationDays: e.target.value })
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSubmit();
              } else if (e.key === "Escape") {
                handleClose();
              }
            }}
            disabled={loading}
            className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 
                     focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 
                     bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100
                     disabled:bg-gray-50 dark:disabled:bg-slate-800 disabled:cursor-not-allowed"
            placeholder="Opcional — ex.: 30"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
            Prazo em dias para retorno do cliente após o último atendimento deste
            serviço.
          </p>
        </div>

        {/* Professional Assignment - Only for barbershops */}
        {companyCategory === "barbershop" && (
          <div>
            <label
              htmlFor="professional"
              className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2"
            >
              Profissional Responsável
            </label>
            <select
              id="professional"
              value={formData.professionalId || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  professionalId: e.target.value
                    ? Number(e.target.value)
                    : null,
                })
              }
              disabled={loading || isProfessional}
              className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 
                       focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 
                       bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100
                       disabled:bg-gray-50 dark:disabled:bg-slate-800 disabled:cursor-not-allowed"
            >
              <option value="">Nenhum profissional específico</option>
              {professionals.map((professional) => (
                <option key={professional.id} value={professional.id}>
                  {professional.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Form Actions */}
        <FormActions
          onCancel={handleClose}
          onSubmit={handleSubmit}
          loading={loading}
          isEditing={!!editingService}
          submitType="button"
          submitText={editingService ? "Atualizar Serviço" : "Criar Serviço"}
        />
      </div>
    </Modal>
  );
};
